// Shared session card used by the plan builder and plan editor.
// Renders one workout "session" (a day + its workout config) with the dynamic
// add/remove/duplicate affordances. Operates purely on a WorkoutDay; callers own
// the surrounding list state.

import '../pages/WorkoutBuilderDemo.css';
import {
  type WorkoutDay,
  DAYS_OF_WEEK,
  TIME_OPTIONS,
  MODALITY_OPTIONS,
  NOTES_MODALITIES,
  NOTES_LABEL,
  NOTES_PLACEHOLDER,
  STRENGTH_FOCUS,
  EQUIPMENT,
  DIFFICULTY_OPTIONS,
  DURATION_OPTIONS,
} from '../lib/workoutOptions';

interface Props {
  session: WorkoutDay;
  onField: <K extends keyof WorkoutDay>(field: K, value: WorkoutDay[K]) => void;
  onModality: (mod: string) => void;
  onTogglePill: (field: 'strength_focus' | 'equipment', val: string) => void;
  onRemove: () => void;
  onDuplicate: () => void;
}

export default function WorkoutSessionCard({
  session, onField, onModality, onTogglePill, onRemove, onDuplicate,
}: Props) {
  return (
    <div className="wb-card">
      <div className="wb-card-header">
        <select
          className="wb-day-select"
          value={session.day}
          onChange={e => onField('day', e.target.value)}
        >
          <option value="">Select day...</option>
          {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <button className="wb-remove-btn" onClick={onRemove} aria-label="Remove">×</button>
      </div>

      <div className="wb-card-body">

        <div className="wb-field">
          <span className="wb-field-label">Session name</span>
          <input type="text" className="wb-input" value={session.name}
            onChange={e => onField('name', e.target.value)}
            placeholder="e.g. Morning Run" maxLength={80} />
        </div>

        <div className="wb-field">
          <span className="wb-field-label">Time</span>
          <select className="wb-select" value={session.time}
            onChange={e => onField('time', e.target.value)}>
            {TIME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div className="wb-field">
          <span className="wb-field-label">Type</span>
          <div className="wb-pills">
            {MODALITY_OPTIONS.map(opt => (
              <button key={opt.value} type="button"
                className={`wb-pill${session.modality === opt.value ? ' wb-pill--active' : ''}`}
                onClick={() => onModality(opt.value)}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {NOTES_MODALITIES.has(session.modality) && (
          <div className="wb-field">
            <span className="wb-field-label">{NOTES_LABEL[session.modality] ?? 'Activity'}</span>
            <input type="text" className="wb-input" value={session.notes}
              onChange={e => onField('notes', e.target.value)}
              placeholder={NOTES_PLACEHOLDER[session.modality] ?? ''} maxLength={80} />
          </div>
        )}

        {session.modality === 'strength' && (
          <div className="wb-sub-section">
            <div className="wb-field">
              <span className="wb-field-label">Focus area</span>
              <div className="wb-pills">
                {STRENGTH_FOCUS.map(opt => (
                  <button key={opt.value} type="button"
                    className={`wb-pill${session.strength_focus === opt.value ? ' wb-pill--active' : ''}`}
                    onClick={() => onTogglePill('strength_focus', opt.value)}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="wb-field">
              <span className="wb-field-label">Equipment</span>
              <div className="wb-pills">
                {EQUIPMENT.map(opt => (
                  <button key={opt.value} type="button"
                    className={`wb-pill${session.equipment === opt.value ? ' wb-pill--active' : ''}`}
                    onClick={() => onTogglePill('equipment', opt.value)}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="wb-field-row">
          <div className="wb-field">
            <span className="wb-field-label">Duration</span>
            <select className="wb-select" value={session.duration_minutes}
              onChange={e => onField('duration_minutes', Number(e.target.value))}>
              {DURATION_OPTIONS.map(m => <option key={m} value={m}>{m} min</option>)}
            </select>
          </div>
          <div className="wb-field">
            <span className="wb-field-label">Level</span>
            <select className="wb-select" value={session.difficulty}
              onChange={e => onField('difficulty', e.target.value as WorkoutDay['difficulty'])}>
              {DIFFICULTY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>

        <div className="wb-field">
          <span className="wb-field-label">Anything else?</span>
          <textarea className="wb-textarea" rows={2} value={session.other_notes}
            onChange={e => onField('other_notes', e.target.value)}
            placeholder="Injuries, equipment limits, goals for this session..."
            maxLength={300} />
        </div>

        <button className="wb-duplicate-btn" onClick={onDuplicate}>
          + Duplicate to another day
        </button>

      </div>
    </div>
  );
}
